// Relationship updates: apply -2..+2 deltas with clamping, insert memories,
// condense to long-term bucket when >5 recent memories per direction.
// Pure helpers (testable) + SQL appliers kept thin.
import type { Memory, Relationship } from '../types.js';
import { TUNING } from '../constants.js';
import { clamp } from '../sim/engine.js';

export interface DeltaSide {
  familiarity: number;
  affinity: number;
  trust: number;
  respect: number;
  fear: number;
  attraction: number;
}

export function blankRelationship(npcId: string, targetId: string): Relationship {
  return { npcId, targetId, familiarity: 0, affinity: 0, trust: 0, respect: 0, fear: 0, attraction: 0 };
}

// True once two NPCs actually have something between them: any non-zero
// score, a memory, or a long-term impression. All-zero records (never talked)
// stay hidden in the bio's relationship tab.
export function hasHistory(rel: Relationship, mems: Memory[], longterm: string): boolean {
  if (longterm.trim().length > 0 || mems.length > 0) return true;
  return (
    rel.familiarity !== 0 ||
    rel.affinity !== 0 ||
    rel.trust !== 0 ||
    rel.respect !== 0 ||
    rel.fear !== 0 ||
    rel.attraction !== 0
  );
}

// Urge to chat: deterministic pull of self toward other, 0-100 for display.
// affinity (A): dislike → ~0, adoration → 1.
// recency (R): just chatted → ~0, long apart (or never) → 1.
// distance (D): adjacent → 1, 300px+ → 0.
// attraction (T): spark multiplier with a 0.3 floor, so genuine friendship
// alone still pulls — a literal zero here would mute all platonic bonds.
export interface UrgeInfo {
  value: number; // 0-100
  a: number;
  r: number;
  d: number;
  t: number;
}

export function urgeToChat(
  affinity: number,
  attraction: number,
  msSinceChat: number | null,
  distPx: number,
): UrgeInfo {
  const a = clamp((affinity + 10) / 20, 0, 1);
  const t = Math.max(0.3, clamp(attraction / 10, 0, 1));
  const r = msSinceChat == null ? 1 : clamp(msSinceChat / 120000, 0, 1);
  const d = clamp(1 - distPx / 300, 0, 1);
  return { value: Math.round(a * r * d * t * 100), a, r, d, t };
}

// Non-zero delta fields for the post-chat +/- floaters (shown over the NPC
// whose feelings shifted). Order matches the bio's relationship display.
const FLOATER_FIELDS = ['familiarity', 'affinity', 'trust', 'respect', 'fear', 'attraction'] as const;

export interface DeltaItem {
  field: string;
  delta: number;
}

export function deltaFloaters(d: DeltaSide): DeltaItem[] {
  return FLOATER_FIELDS.map((field) => ({ field, delta: d[field] })).filter((i) => i.delta !== 0);
}

export function applyDelta(rel: Relationship, d: DeltaSide): Relationship {
  return {
    ...rel,
    familiarity: clamp(rel.familiarity + Math.max(0, d.familiarity), 0, 10),
    affinity: clamp(rel.affinity + d.affinity, -10, 10),
    trust: clamp(rel.trust + d.trust, -10, 10),
    respect: clamp(rel.respect + d.respect, -10, 10),
    fear: clamp(rel.fear + d.fear, 0, 10),
    attraction: clamp(rel.attraction + d.attraction, 0, 10),
  };
}

// Split memories into kept (newest KEEP) + bumped (older, fold into longterm).
export function splitForCondense<T extends { createdAt: string }>(
  memsNewestFirst: T[],
): { kept: T[]; bumped: T[] } {
  const k = TUNING.recentMemoryKeep;
  if (memsNewestFirst.length <= k) return { kept: memsNewestFirst, bumped: [] };
  return {
    kept: memsNewestFirst.slice(0, k),
    bumped: memsNewestFirst.slice(k),
  };
}

export function sortNewestFirst(mems: Memory[]): Memory[] {
  return [...mems].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}
